import React, { useMemo, useState } from "react";
import styled from "styled-components";

import { Card, SectionHeader, Tag, Button } from "../../../../design-system";
import { OutreachLead } from "../../../../types/OutreachMetrics";
import {
  buildLinkedInDayPlan,
  LinkedInSequence,
  LinkedInStep,
  LinkedInStepStatus,
} from "./linkedinSequence";

// The LinkedIn counterpart to the Instagram paced sequences card. Instagram
// reads stored sequence documents; LinkedIn's hourly drip keeps its plan on the
// lead itself, so this card projects that plan (see linkedinSequence.ts) and
// leads with what the drip will do TODAY. Read-only on purpose: the drip owns
// the state machine, and controls here would be a second, conflicting source
// of truth.

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  margin-bottom: var(--space-4);
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
`;

const Sequence = styled.div<{ $today: boolean }>`
  padding: var(--space-4);
  border: 1px solid
    ${(p) => (p.$today ? "var(--color-accent)" : "var(--color-border)")};
  border-radius: var(--radius-md);
  background: var(--color-surface-subtle);
`;

const SequenceHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
`;

const Name = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Meta = styled.div`
  margin-top: 2px;
  font-size: var(--font-size-xs);
  color: var(--color-text-tertiary);
`;

const Today = styled.div<{ $tone: "act" | "block" | "idle" }>`
  margin-top: var(--space-3);
  font-size: var(--font-size-sm);
  color: ${(p) =>
    p.$tone === "block"
      ? "var(--color-danger)"
      : p.$tone === "act"
      ? "var(--color-text)"
      : "var(--color-text-secondary)"};
`;

const Steps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-2);
  margin-top: var(--space-3);

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const Step = styled.div`
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
`;

const StepTitle = styled.div`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const StepWhen = styled.div`
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
`;

const StepDetail = styled.div`
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

const Empty = styled.p`
  margin: 0;
  color: var(--color-text-tertiary);
  font-size: var(--font-size-sm);
`;

const STATE_LABEL: Record<LinkedInSequence["state"], string> = {
  to_invite: "To invite",
  awaiting_accept: "Awaiting acceptance",
  await_fu2: "Connected",
  // The post-demo ladder (2026-09-14): the demo is out and the drip owns the
  // two bumps that follow it.
  demo_await_1: "Demo sent — bump 1",
  demo_await_2: "Demo sent — bump 2",
  done: "Finished",
  reply_due: "Replied — reply due",
  human: "Human owns it",
  handed_back: "Handed back",
  quarantined: "ICP quarantine",
  deferred: "Deferred",
  unknown: "Unknown state",
};

const stateVariant = (state: LinkedInSequence["state"]) =>
  state === "await_fu2" || state === "reply_due"
    ? "success"
    : state === "human" ||
      state === "handed_back" ||
      state === "unknown" ||
      state === "deferred"
    ? "warning"
    : state === "done" || state === "quarantined"
    ? "neutral"
    : "accent";

const stepVariant = (status: LinkedInStepStatus) =>
  status === "sent"
    ? "success"
    : status === "blocked"
    ? "danger"
    : status === "due"
    ? "accent"
    : "neutral";

const StepCard: React.FC<{ step: LinkedInStep }> = ({ step }) => (
  <Step>
    <StepTitle>{step.label}</StepTitle>
    <StepWhen>{step.when}</StepWhen>
    <div style={{ marginTop: "var(--space-2)" }}>
      <Tag variant={stepVariant(step.status)} size="sm">
        {step.status}
      </Tag>
    </div>
    {step.detail && <StepDetail>{step.detail}</StepDetail>}
  </Step>
);

const PAGE = 8;

const LinkedInLeadSequences: React.FC<{
  leads: OutreachLead[];
  onSelectLead?: (leadId: string) => void;
}> = ({ leads, onSelectLead }) => {
  const [showAll, setShowAll] = useState(false);
  const plan = useMemo(() => buildLinkedInDayPlan(leads), [leads]);
  const visible = showAll ? plan.sequences : plan.sequences.slice(0, PAGE);

  return (
    <Card padding="lg">
      <SectionHeader
        title="LinkedIn paced sequences"
        description="Connection request, then follow-up 1 on acceptance, then follow-up 2. The hourly drip sends them. This is what it plans to do today and where each lead is stuck."
        actions={
          plan.sequences.length > PAGE ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Show fewer" : `Show all ${plan.sequences.length}`}
            </Button>
          ) : undefined
        }
      />
      <Summary>
        <Tag size="sm" variant={plan.inWindow ? "success" : "neutral"} withDot>
          {plan.inWindow ? "Send window open" : "Outside send window"}
        </Tag>
        <span>
          {plan.windowLabel} · up to {plan.invitesCapPerTick} invites and{" "}
          {plan.messagesCapPerTick} messages per hourly tick
        </span>
        <span>
          Today: <strong>{plan.invitesToday}</strong> invite
          {plan.invitesToday === 1 ? "" : "s"} ·{" "}
          <strong>{plan.messagesToday}</strong> follow-up
          {plan.messagesToday === 1 ? "" : "s"}
        </span>
        {plan.blocked > 0 && (
          <Tag size="sm" variant="danger">
            {plan.blocked} need a human
          </Tag>
        )}
        <span>{plan.live} live</span>
      </Summary>

      {visible.length === 0 ? (
        <Empty>No LinkedIn leads in the drip for this account.</Empty>
      ) : (
        <List>
          {visible.map((sequence) => (
            <Sequence
              key={sequence.leadId}
              $today={sequence.actsToday}
              onClick={
                onSelectLead ? () => onSelectLead(sequence.leadId) : undefined
              }
              style={onSelectLead ? { cursor: "pointer" } : undefined}
            >
              <SequenceHead>
                <div>
                  <Name>{sequence.name}</Name>
                  <Meta>
                    {sequence.username ? `@${sequence.username}` : "no username"}
                    {sequence.nextActionAt
                      ? ` · next ${sequence.nextActionAt}`
                      : " · unscheduled"}
                    {typeof sequence.pendingDays === "number"
                      ? ` · invited ${sequence.pendingDays}d ago`
                      : ""}
                  </Meta>
                </div>
                <Tag variant={stateVariant(sequence.state)} size="sm" withDot>
                  {STATE_LABEL[sequence.state]}
                </Tag>
              </SequenceHead>

              <Today
                $tone={
                  sequence.actsToday
                    ? "act"
                    : sequence.needsHuman
                    ? "block"
                    : "idle"
                }
              >
                {sequence.today}
              </Today>

              <Steps>
                {sequence.steps.map((step) => (
                  <StepCard key={step.type} step={step} />
                ))}
              </Steps>
            </Sequence>
          ))}
        </List>
      )}
    </Card>
  );
};

export default LinkedInLeadSequences;
